import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { PolicyModel, PolicyToUpdateModel } from 'src/app/models/api/governance/policy.model';
import { MatDialog } from '@angular/material/dialog';
import { AddLocationDialogComponent } from '../add-location-dialog/add-location-dialog.component';
import { IntegrationModel } from 'src/app/models/api';
import _ from 'lodash';
import { StatusCodeEnum } from 'src/app/models/api/integration.model';
import { InactiveIntegrationDialogComponent } from '../inactive-integration-dialog/inactive-integration-dialog.component';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { SensitiveLocationViewModel } from 'src/app/models/viewModels/sensitiveLocationsViewModel.interface';
import { IWarningDialogInput, WarningDialogComponent } from '../warning-dialog/warning-dialog.component';

@Component({
  selector: 'app-policy-sensitive-locations',
  templateUrl: './policy-sensitive-locations.component.html',
  styleUrls: ['./policy-sensitive-locations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
        position: -webkit-sticky;
        position: sticky;
        top: 0px;
      }

      @media screen and (max-width: 64em) {
        :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
          top: 0px;
        }
      }
    `,
  ],
})
export class PolicySensitiveLocationsComponent implements OnInit {
  @Input() public policyModel: PolicyModel;
  @Input() public integrationModel: IntegrationModel;
  @Input() public editable = true;
  @Input() public tableNotFoundText = 'No results found';
  @Input() public tableNotFoundIcon = 'assets/images/common/no-results-found.svg';
  @Output() public goBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() public policyUpdated: EventEmitter<PolicyModel> = new EventEmitter<PolicyModel>();
  @Output() public notifyChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  public sensitiveLocationsStartState: string[];
  public locationsForSearchBox: string[] = [];

  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public changesWereDone = false;

  public locationsCurrentPage$: BehaviorSubject<SensitiveLocationViewModel[]> = new BehaviorSubject<
    SensitiveLocationViewModel[]
  >([]);
  public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<
    string[] | undefined
  >();

  public readonly MAX_LOCATIONS = 15;

  private readonly destroyRef = inject(DestroyRef);
  private _locationsViewModel: SensitiveLocationViewModel[] = [];

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _notificationService: NotificationsService
  ) {
    this.deleteLocations = this.deleteLocations.bind(this);
  }

  ngOnInit(): void {
    this.sensitiveLocationsStartState = this.policyModel.config.sensitiveLocations?.slice() || [];
    this._locationsViewModel = this.sensitiveLocationsStartState.map((location) =>
      this._transformToLocationViewModel(location)
    );
    this._updateLocationsViewModel();
  }

  public get locationsCount(): number {
    return this._locationsViewModel.length;
  }

  public get isMaxLocationsReached(): boolean {
    return this.locationsCount >= this.MAX_LOCATIONS;
  }

  public addLocationClicked() {
    if (this.isMaxLocationsReached) {
      this._notificationService.warning(`Maximum of ${this.MAX_LOCATIONS} locations can be selected.`);
      return;
    }

    // Create a copy of policy model with current locations (including unsaved ones)
    const currentLocations = this._locationsViewModel.map((loc) => loc.rowId);
    const policyDataWithCurrentLocations = {
      ...this.policyModel,
      config: {
        ...this.policyModel.config,
        sensitiveLocations: currentLocations,
      },
    };

    const dialogRef = this._matDialog.open(AddLocationDialogComponent, {
      data: policyDataWithCurrentLocations,
      disableClose: false,
      autoFocus: false,
      panelClass: 'dynamic-size-popup-dialog-container',
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: { newLocations: string[] } | undefined) => {
        if (result && result.newLocations && result.newLocations.length > 0) {
          const remainingSlots = this.MAX_LOCATIONS - this.locationsCount;
          // Trim spaces from all locations
          const trimmedLocations = result.newLocations.map((location) => location.trim());
          const locationsToAdd = trimmedLocations.slice(0, remainingSlots);

          if (result.newLocations.length > remainingSlots) {
            this._notificationService.warning(
              `Only ${remainingSlots} location${remainingSlots > 1 ? 's' : ''} can be added. Maximum of ${this.MAX_LOCATIONS} locations allowed.`
            );
          }

          if (locationsToAdd.length > 0) {
            this._locationsViewModel.push(
              ...locationsToAdd.map((location) => this._transformToLocationViewModel(location))
            );
            this._updateLocationsViewModel();
            this.changesWereDone = true;
            this.notifyChanges.emit(this.changesWereDone);

            const message = `${locationsToAdd.length} new location${locationsToAdd.length > 1 ? 's' : ''} will be added.`;
            this._notificationService.success(message);
          }
        }
      });
  }

  public resetForm() {
    this._locationsViewModel = this.sensitiveLocationsStartState.map((location) =>
      this._transformToLocationViewModel(location)
    );
    this._updateLocationsViewModel();
    this.resetSearchBoxBehavioralSubject$.next(this.sensitiveLocationsStartState);
    this.changesWereDone = false;
    this.notifyChanges.emit(this.changesWereDone);
  }

  public deleteLocations(locations: string[]): Observable<string> {
    const locationsAfterDeletion = this.sensitiveLocationsStartState.filter(
      (location) => !locations.includes(location)
    );
    return this._policiesHttpService.updatePolicy(
      this.integrationModel.id,
      this.policyModel.id,
      this._generateUpdatePolicyModel(locationsAfterDeletion)
    );
  }

  public locationSearched(locations: string[]) {
    const locationsViewModel = this._locationsViewModel
      .map((locationViewModel) => this._transformToLocationViewModel(locationViewModel.url))
      .filter((locationViewModel) => locations.includes(locationViewModel.url))
      .sort((a, b) => a.url.toLowerCase().localeCompare(b.url.toLowerCase()));
    this.locationsCurrentPage$.next(locationsViewModel);
  }

  public updatePolicyConfig() {
    if (this.integrationModel.connectionStatus.code === StatusCodeEnum.off) {
      const dialogRef = this._matDialog.open(InactiveIntegrationDialogComponent, {
        data: this.integrationModel,
        disableClose: false,
        panelClass: 'dynamic-size-popup-dialog-container',
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this._updatePolicyConfig();
        });
    } else {
      this._updatePolicyConfig();
    }
  }

  public cancelSettings() {
    this.goBack.emit();
  }

  public removeLocationClicked(locationToRemove: SensitiveLocationViewModel): void {
    const dialogRef = this._matDialog.open(WarningDialogComponent, {
      data: {
        elementsIds: [locationToRemove.rowId],
        elementsNames: [locationToRemove.url],
        title: 'policyPage.config.sensitiveLocationsContent.deleteLocation',
        notificationMsgLabelPath: 'common.wasDeletedSuccessfully',
        actionCallback: this.deleteLocations,
      } as IWarningDialogInput,
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locationDeleted?: boolean) => {
        if (locationDeleted) {
          const index = this._locationsViewModel.indexOf(locationToRemove);
          if (index > -1) this._locationsViewModel.splice(index, 1);

          const locationIndex = this.policyModel.config.sensitiveLocations?.indexOf(locationToRemove.rowId);
          if (locationIndex !== undefined && locationIndex > -1) {
            this.policyModel.config.sensitiveLocations?.splice(locationIndex, 1);
          }

          this._updateLocationsViewModel();
          this.policyModel.config.sensitiveLocations = this._locationsViewModel.map((location) => location.rowId);

          // Update start state to reflect the deletion for subsequent deletes
          this.sensitiveLocationsStartState = this.policyModel.config.sensitiveLocations.slice();

          this.policyUpdated.emit(this.policyModel);
        }
        this.resetSearchBoxBehavioralSubject$.next(this.sensitiveLocationsStartState);
      });
  }

  private _updateLocationsViewModel() {
    const sortedLocations = [...this._locationsViewModel].sort((a, b) =>
      a.url.toLowerCase().localeCompare(b.url.toLowerCase())
    );
    this.locationsCurrentPage$.next(sortedLocations);
    this.locationsForSearchBox = sortedLocations.map((location) => location.url);
  }

  private _transformToLocationViewModel(url: string): SensitiveLocationViewModel {
    return {
      rowId: url,
      url: url,
    };
  }

  private _updatePolicyConfig() {
    this.isLoading$.next(true);
    const locations = this._locationsViewModel.map((locationViewModel) => locationViewModel.rowId);

    const previousLocations = new Set(this.sensitiveLocationsStartState);
    const currentLocations = new Set(locations);
    const addedLocations = locations.filter((loc) => !previousLocations.has(loc));
    const removedLocations = this.sensitiveLocationsStartState.filter((loc) => !currentLocations.has(loc));

    this._policiesHttpService
      .updatePolicy(this.integrationModel.id, this.policyModel.id, this._generateUpdatePolicyModel(locations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          let message = 'Policy updated successfully.';
          if (addedLocations.length > 0) {
            message += ` ${addedLocations.length} new location${addedLocations.length > 1 ? 's' : ''} added.`;
          }
          if (removedLocations.length > 0) {
            message += ` ${removedLocations.length} location${removedLocations.length > 1 ? 's' : ''} removed.`;
          }

          this._notificationService.success(message);
          this._getPolicyUpdates();
        },
        error: (error) => {
          this._httpErrorHandlerService.handle(error);
          this.isLoading$.next(false);
        },
      });
  }

  private _getPolicyUpdates() {
    this._policiesHttpService
      .getPolicy(this.integrationModel.id, this.policyModel.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((policy) => {
        this.policyModel = policy;
        this.sensitiveLocationsStartState = this.policyModel.config.sensitiveLocations?.slice() || [];
        this._locationsViewModel = this.sensitiveLocationsStartState.map((location) =>
          this._transformToLocationViewModel(location)
        );
        this._updateLocationsViewModel();
        this.changesWereDone = false;
        this.notifyChanges.emit(this.changesWereDone);
        this.policyUpdated.emit(this.policyModel);
        this.isLoading$.next(false);
      });
  }

  private _generateUpdatePolicyModel(locations?: string[]): PolicyToUpdateModel {
    const config = _.cloneDeep(this.policyModel.config);
    config.sensitiveLocations = locations ? locations : this.sensitiveLocationsStartState;
    return {
      name: this.policyModel.name,
      description: this.policyModel.description,
      type: this.policyModel.isDefault ? undefined : this.policyModel.type,
      config: config,
    } as PolicyToUpdateModel;
  }
}
